/**
 * CCR.js
 *
 * Copyright (c) 2026 Eric Seigne <eric.seigne@cap-rel.fr>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

import { debugCCR } from '../utils/debug.js'
import { Gas } from './Gas.js'
import { Buhlmann } from './Buhlmann.js'
import { PlanError, PLAN_ERROR_CODES } from './errors.js'

// CCR (Closed Circuit Rebreather) utilities
// Handles setpoint management and equivalent gas calculations
//
// Every depth <-> pressure conversion goes through Gas.pressureAtDepth /
// Gas.depthAtPressure, so the loop mix runs on the same physical pressure scale
// as the decompression model (1.01325 bar at sea level, 0.101043 bar per metre
// in salt water, see Gas.js) and gets the same altitude correction.
// env = { altitude: <metres>, waterType: <preset key or density in g/l> }, both
// optional; the defaults are sea level and salt water, and Gas.waterDensity()
// lists the accepted water types.

export class CCR {
  // Setpoint modes
  static MODES = {
    FIXED: 'fixed',
    DYNAMIC: 'dynamic'
  }

  // How far the real ppO2 may sit from the setpoint and still count as held,
  // in bar. Half of the last digit the plan prints: below that the two columns
  // read the same, above it they would contradict each other.
  static SETPOINT_TOLERANCE = 0.005

  // Dive phases for dynamic setpoint
  static PHASES = {
    DESCENT: 'descent',
    BOTTOM: 'bottom',
    ASCENT: 'ascent'
  }

  // Default setpoints in bar, applied only when the caller provides nothing
  static DEFAULT_SETPOINTS = Object.freeze({
    fixed: 1.3,
    descent: 0.7,
    bottom: 1.3,
    ascent: 1.0
  })

  // Setpoint range accepted by the planner, in bar (both bounds included).
  // Same range as the plan_error_invalid_setpoint message shown to the diver.
  static MIN_SETPOINT = 0.4
  static MAX_SETPOINT = 1.6

  // Default diluent when the caller provides none: air
  static DEFAULT_DILUENT = Object.freeze({ name: 'Air', fO2: 0.21, fHe: 0, fN2: 0.79 })

  // Keep the default ONLY when the value is absent. A 0, NaN or empty setpoint
  // used to be swallowed by `config.fixedSetpoint || 1.3`, so the plan was
  // computed at 1.3 bar while the configuration displayed something else.
  // Invalid values are kept as is and rejected by validateCCRConfig.
  static resolveSetpoint(value, defaultValue) {
    return (value === undefined || value === null) ? defaultValue : value
  }

  // Validate and complete a diluent: fN2 is what the model actually breathes,
  // and a diluent literal built by a view or restored from storage frequently
  // lacks it. Throws PlanError after logging when the mix is unusable.
  static normalizeDiluent(diluent, context = 'CCR') {
    if (diluent === undefined || diluent === null) {
      return { ...CCR.DEFAULT_DILUENT }
    }
    if (typeof diluent !== 'object') {
      console.error('%s: diluent is not an object (%o)', context, diluent)
      throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, { context, diluent })
    }
    // pureO2Label = false: a diluent is never pure oxygen, so the generated
    // name stays in the EANxx form instead of reading 'O2'
    return Gas.normalizeMix(diluent, false)
  }

  // Inert fractions of a diluent for the static loop calculations, guarded.
  // Derives fN2 when absent and refuses any non finite or negative fraction:
  // a NaN here propagates straight into the tissue loading and yields a plan
  // with almost no decompression stop.
  static inertFractions(diluent, context) {
    if (!diluent || typeof diluent !== 'object') {
      console.error('%s: no usable diluent (%o)', context, diluent)
      throw new PlanError(PLAN_ERROR_CODES.NO_DILUENT, { context, diluent })
    }
    const fHe = (diluent.fHe === undefined || diluent.fHe === null) ? 0 : diluent.fHe
    const fN2 = (diluent.fN2 === undefined || diluent.fN2 === null)
      ? 1 - diluent.fO2 - fHe
      : diluent.fN2
    Gas.assertBreathingMix({ name: diluent.name, fO2: diluent.fO2, fN2, fHe }, context)
    return { fN2, fHe }
  }

  constructor(config = {}) {
    this.mode = config.mode || CCR.MODES.FIXED
    // Fixed mode setpoint
    this.fixedSetpoint = CCR.resolveSetpoint(config.fixedSetpoint, CCR.DEFAULT_SETPOINTS.fixed)
    // Dynamic mode setpoints
    this.dynamicSetpoints = {
      descent: CCR.resolveSetpoint(config.descentSetpoint, CCR.DEFAULT_SETPOINTS.descent),
      bottom: CCR.resolveSetpoint(config.bottomSetpoint, CCR.DEFAULT_SETPOINTS.bottom),
      ascent: CCR.resolveSetpoint(config.ascentSetpoint, CCR.DEFAULT_SETPOINTS.ascent)
    }
    // Diluent gas (used in CCR loop), always carrying its fN2 and its name
    this.diluent = CCR.normalizeDiluent(config.diluent, 'CCR constructor')
    // Dive environment, shared with the decompression model.
    // Not persisted by toJSON: it belongs to the global environment settings,
    // and DivePlanner.planCCR pushes its own environment in before planning.
    this.env = config.environment || {}
    debugCCR('CCR created: mode=%s, fixedSP=%s, diluent=%o, altitude=%om, waterType=%o',
      this.mode, this.fixedSetpoint, this.diluent, this.env.altitude || 0, this.env.waterType ?? 'salt')
  }

  // Change the dive environment used by every pressure calculation
  setEnvironment(env) {
    this.env = env || {}
    debugCCR('setEnvironment: altitude=%om, waterType=%o', this.env.altitude || 0, this.env.waterType ?? 'salt')
  }

  // Get setpoint for current phase.
  // An unknown phase falls back to the fixed setpoint; a phase setpoint that is
  // present but invalid is returned as is, so validateCCRConfig rejects the
  // plan instead of silently computing it at the fixed setpoint.
  getSetpoint(phase = CCR.PHASES.BOTTOM) {
    if (this.mode === CCR.MODES.FIXED) {
      return this.fixedSetpoint
    }
    const phaseSetpoint = this.dynamicSetpoints[phase]
    return (phaseSetpoint === undefined || phaseSetpoint === null) ? this.fixedSetpoint : phaseSetpoint
  }

  // Calculate equivalent open circuit gas for CCR at depth
  // In CCR, ppO2 is fixed by setpoint, rest is diluent inert gases
  // Water vapor pressure (0.0627 bar) is subtracted from inspired gas
  static calcEquivalentGas(depth, setpoint, diluent, env = {}) {
    const pAmb = Gas.pressureAtDepth(depth, env) // Ambient pressure in bar
    // Same constant as the tissue loading code: the fractions computed here are
    // fed to Buhlmann.loadTissues, which subtracts that very water vapor pressure
    const pH2O = Buhlmann.WATER_VAPOR_PRESSURE

    // Diluent inert gas fractions (guarded: a missing fN2 used to give NaN)
    const { fN2: dilN2, fHe: dilHe } = CCR.inertFractions(diluent, 'CCR.calcEquivalentGas')

    // ppO2 is the setpoint, capped by the ambient pressure at shallow depths
    // and floored by the loop itself when it holds no inert gas at all.
    //
    // That floor is the physics of the machine. A controller only ever ADDS
    // oxygen; for a ppO2 to come down, metabolism has to consume the oxygen and
    // something inert has to take its place. On a loop flushed with pure
    // oxygen there is nothing inert to take that place, and the ADV can only
    // put more oxygen in, so the loop sits at ambient pressure whatever the
    // setpoint says. Without this floor the plan printed 1.30 bar on stops
    // really breathed at 1.62, and the CNS clock - which reads this very ppO2 -
    // charged 0.56 %/min instead of 2.50: 16 % instead of 70 % on a 28 min stop.
    //
    // Only the no inert case is floored. With a diluent that does carry inert
    // gas the current model is right: the loop keeps accumulating inert as the
    // ADV tops it up, which is exactly how a rebreather holds a setpoint well
    // below what its diluent would give on its own (air at 60 m, 1.49 bar, held
    // at 1.30).
    const ppO2 = dilN2 + dilHe > 0 ? Math.min(setpoint, pAmb) : pAmb

    // Remaining pressure is filled by diluent inert gases (minus water vapor)
    // This matches MVPlan Java: pInert = pAmb - pO2*pConversion - pH2O
    const pInert = Math.max(0, pAmb - ppO2 - pH2O)

    const diluentInertFraction = dilN2 + dilHe
    const fN2Ratio = dilN2 / (diluentInertFraction || 1)
    const fHeRatio = dilHe / (diluentInertFraction || 1)

    // Actual partial pressures of inert gases
    const ppN2 = pInert * fN2Ratio
    const ppHe = pInert * fHeRatio

    // Convert to fractions relative to inspired gas (pAmb - pH2O)
    // This ensures loadTissues() gets correct values when it does fN2 * (pAmb - pH2O)
    const pInspired = pAmb - pH2O
    const result = {
      fO2: ppO2 / pAmb,
      fN2: ppN2 / pInspired,
      fHe: ppHe / pInspired,
      ppO2: ppO2,
      ppN2: ppN2,
      ppHe: ppHe
    }
    debugCCR('calcEquivalentGas: depth=%dm, SP=%s -> ppO2=%s, ppN2=%s, ppHe=%s', depth, setpoint, ppO2.toFixed(2), ppN2.toFixed(2), ppHe.toFixed(2))
    return result
  }

  // Get equivalent gas for current configuration at depth
  getEquivalentGas(depth, phase = CCR.PHASES.BOTTOM) {
    const setpoint = this.getSetpoint(phase)
    return CCR.calcEquivalentGas(depth, setpoint, this.diluent, this.env)
  }

  // The setpoint the loop really holds at this depth, or null when it holds
  // none. Printed in the Setpoint column of the plan, where it must not
  // contradict the ppO2 column right next to it: a stop breathed at 1.62 bar
  // on a loop of pure oxygen used to print "SP 1.3", two numbers that cannot
  // both be true. Same rule for water shallower than the setpoint itself,
  // where ambient pressure is all the loop can give.
  //
  // Judged on the computed ppO2 and not on the diluent, so there is one rule
  // and calcEquivalentGas stays the only place that decides what the loop is
  // worth.
  heldSetpoint(depth, phase = CCR.PHASES.BOTTOM) {
    const setpoint = this.getSetpoint(phase)
    const { ppO2 } = CCR.calcEquivalentGas(depth, setpoint, this.diluent, this.env)
    return Math.abs(ppO2 - setpoint) <= CCR.SETPOINT_TOLERANCE ? setpoint : null
  }

  // Loop gas fractions (sum to 1) for density / narcosis displays.
  // Unlike calcEquivalentGas (which keeps fN2/fHe normalized to pAmb-pH2O for
  // tissue loading), this returns fractions normalized to pAmb so that
  // fO2 + fN2 + fHe = 1 - required by Gas.gasDensity.
  static calcLoopGasMix(depth, setpoint, diluent, env = {}) {
    const pAmb = Gas.pressureAtDepth(depth, env)
    const { fN2: dilN2, fHe: dilHe } = CCR.inertFractions(diluent, 'CCR.calcLoopGasMix')
    const inertFraction = dilN2 + dilHe
    // Same floor as calcEquivalentGas, for the same reason: a loop holding no
    // inert gas is oxygen at ambient pressure, whatever the setpoint says.
    // Read as a setpoint, it produced a mix 20 % nitrogen at a 1.3 bar
    // setpoint 6 m down, and the density and the END of those stops were
    // computed on an inert gas the loop does not contain.
    const ppO2 = inertFraction > 0 ? Math.min(setpoint, pAmb) : pAmb
    const pInert = Math.max(0, pAmb - ppO2)
    const fO2 = ppO2 / pAmb
    const fHe = inertFraction > 0
      ? (pInert / pAmb) * (dilHe / inertFraction)
      : 0
    const fN2 = Math.max(0, 1 - fO2 - fHe)
    return { fO2, fN2, fHe }
  }

  getLoopGasMix(depth, phase = CCR.PHASES.BOTTOM) {
    return CCR.calcLoopGasMix(depth, this.getSetpoint(phase), this.diluent, this.env)
  }

  // Calculate MOD for a given setpoint (max operating depth)
  static calcMOD(setpoint, ppO2Max = 1.6) {
    // In CCR, setpoint IS the ppO2, so MOD is unlimited unless setpoint > ppO2Max
    // Practically, MOD is where we can still maintain the setpoint
    // For very shallow water, ppO2 = ambient pressure
    if (setpoint > ppO2Max) {
      return 0 // Setpoint itself exceeds limit
    }
    return Infinity // CCR can go to any depth with valid setpoint
  }

  // Calculate minimum depth where setpoint can be maintained
  static calcMinDepth(setpoint, env = {}) {
    // Minimum depth where ambient pressure >= setpoint
    return Math.max(0, Gas.depthAtPressure(setpoint, env))
  }

  // Depth from which the loop holds inert gas at this setpoint: where the
  // ambient pressure exceeds the setpoint plus the water vapour pressure,
  // i.e. where calcEquivalentGas stops clamping pInert at zero. Shallower
  // than that the loop is oxygen and water vapour only. Not calcMinDepth:
  // that one ignores the vapour and sits 0.6 m too shallow, which is enough
  // to load the fast compartments of a descent by a hundredth of a bar.
  static inertOnsetDepth(setpoint, env = {}) {
    return Math.max(0, Gas.depthAtPressure(setpoint + Buhlmann.WATER_VAPOR_PRESSURE, env))
  }

  // Calculate END (Equivalent Narcotic Depth) for CCR at depth
  // Only N2 is narcotic (O2 has protective effect per Lafère 2014)
  // Note: Water vapor is NOT included in END calculation (only affects tissue loading)
  static calcEND(depth, setpoint, diluent, env = {}) {
    const pAmb = Gas.pressureAtDepth(depth, env) // Ambient pressure in bar
    const ppO2 = Math.min(setpoint, pAmb)

    // Calculate N2 fraction without water vapor correction (for narcosis)
    const pInert = pAmb - ppO2
    const { fN2: dilN2, fHe: dilHe } = CCR.inertFractions(diluent, 'CCR.calcEND')
    const diluentInertFraction = dilN2 + dilHe
    const fN2Ratio = dilN2 / (diluentInertFraction || 1)
    const fN2_eq = (pInert * fN2Ratio) / pAmb

    // END = depth equivalent of the N2 partial pressure, i.e.
    // depthAtPressure(fN2_eq * pAmb / 0.79). Written in expanded form to stay
    // bit-exact with the historical sea level formula (fN2 * pAmb / 0.79 * 10 - 10)
    const depthPerBar = Gas.depthPerBar(env.waterType)
    const surfaceOffset = Gas.surfacePressure(env.altitude) * depthPerBar
    return Math.round(fN2_eq * pAmb / Gas.AIR_N2_FRACTION * depthPerBar - surfaceOffset)
  }

  // Get END for current configuration
  getEND(depth, phase = CCR.PHASES.BOTTOM) {
    const setpoint = this.getSetpoint(phase)
    return CCR.calcEND(depth, setpoint, this.diluent, this.env)
  }

  // Clone configuration
  clone() {
    return new CCR({
      mode: this.mode,
      fixedSetpoint: this.fixedSetpoint,
      descentSetpoint: this.dynamicSetpoints.descent,
      bottomSetpoint: this.dynamicSetpoints.bottom,
      ascentSetpoint: this.dynamicSetpoints.ascent,
      diluent: { ...this.diluent },
      environment: { ...this.env }
    })
  }

  // Serialize for storage
  toJSON() {
    return {
      mode: this.mode,
      fixedSetpoint: this.fixedSetpoint,
      dynamicSetpoints: { ...this.dynamicSetpoints },
      diluent: { ...this.diluent }
    }
  }

  // Deserialize from storage. The constructor normalizes the stored diluent
  // (fN2 and name) and refuses an unusable mix, so a plan restored from an old
  // or hand edited storage entry can never be computed on a NaN diluent.
  static fromJSON(data) {
    if (!data) return new CCR()
    return new CCR({
      mode: data.mode,
      fixedSetpoint: data.fixedSetpoint,
      descentSetpoint: data.dynamicSetpoints?.descent,
      bottomSetpoint: data.dynamicSetpoints?.bottom,
      ascentSetpoint: data.dynamicSetpoints?.ascent,
      diluent: data.diluent
    })
  }
}
